'use client';
import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import {
  DesignsToken,
  DesignsTokenTheme,
  fetchTheme,
} from '@repo/tokens-studio';
import { useBoolean, useLocalStorage } from 'usehooks-ts';
import z from 'zod';

import { ThemeDispatcher } from './ThemeDispatcher';

const KThemeModeValues = ['LIGHT', 'DARK'] as const;
export type KThemeMode = (typeof KThemeModeValues)[number];

const KThemeVariantValues = ['BRAND1'] as const;
export type KThemeVariant = (typeof KThemeVariantValues)[number];

const THEME_CONFIG: Record<
  KThemeVariant,
  Record<KThemeMode, DesignsTokenTheme>
> = {
  BRAND1: {
    LIGHT: DesignsTokenTheme.LIGHT,
    DARK: DesignsTokenTheme.DARK,
  },
};

export interface IThemeMeta {
  variant: KThemeVariant;
  mode: KThemeMode;
}

interface IThemeSelectContext {
  theme: IThemeMeta;
  tokens: DesignsToken;
  isThemeLoaded: boolean;
  isDarkMode: boolean;
  switchTheme(theme: Partial<IThemeMeta>): Promise<void>;
  availableThemes: Array<KThemeVariant>;
}

const defaultTheme = {
  variant: 'BRAND1',
  mode: 'LIGHT',
} as const;
const ThemeSelectContext = createContext<IThemeSelectContext>({
  theme: defaultTheme,
  isThemeLoaded: false,
  tokens: undefined as unknown as DesignsToken,
  isDarkMode: false,
  switchTheme: async () => {},
  availableThemes: [],
});

export interface IThemeProviderProps {
  initial: Pick<IThemeSelectContext, 'theme' | 'tokens'>;
}

export const ThemeProvider: FC<PropsWithChildren<IThemeProviderProps>> = ({
  initial,
  children,
}) => {
  const [theme, setTheme] = useLocalStorage<IThemeSelectContext['theme']>(
    'theme',
    initial.theme,
    {
      deserializer: (valueString) => {
        try {
          return z
            .object({
              variant: z.enum(KThemeVariantValues),
              mode: z.enum(KThemeModeValues),
            })
            .parse(JSON.parse(valueString));
        } catch {
          return defaultTheme;
        }
      },
    },
  );
  const { value: isLoaded, setTrue: markAsLoaded } = useBoolean(false);
  const [tokens, setTokens] = useState<IThemeSelectContext['tokens']>(
    initial.tokens,
  );

  const fetchAndSetTheme = useCallback<IThemeSelectContext['switchTheme']>(
    async ({ variant = theme.variant, mode = theme.mode }) => {
      setTheme({
        variant,
        mode,
      });
    },
    [theme, setTheme, setTokens],
  );

  useEffect(() => {
    fetchTheme(THEME_CONFIG[theme.variant][theme.mode])
      .then(setTokens)
      .then(markAsLoaded);
  }, [theme]);

  const isDarkMode = theme.mode === 'DARK';

  return (
    <ThemeSelectContext.Provider
      value={{
        theme,
        tokens,
        isThemeLoaded: isLoaded,
        isDarkMode,
        switchTheme: fetchAndSetTheme,
        availableThemes: Object.keys(THEME_CONFIG) as Array<KThemeVariant>,
      }}
    >
      <ThemeDispatcher theme={tokens}>{children}</ThemeDispatcher>
    </ThemeSelectContext.Provider>
  );
};

export const useDesignsTokenTheme = () =>
  useContext(ThemeSelectContext) as IThemeSelectContext;
